// Drop-in replacement for <ChatMarkdown value={body_markdown}/> inside the
// executive summary renderers. Pre-processes the body to collapse long
// citation runs into placeholder anchors, then overrides the markdown <a>
// renderer so:
//   - Normal anchors (e.g., [text](https://example.com))  → default <a>
//   - Single citation anchors `[[N]](#exec-cite-N)`       → inline chip
//   - Collapsed run placeholders                          → <CitationRun>
//
// Every other behavior of ChatMarkdown (lists, headings, formatting) is
// preserved.

import { useMemo, type AnchorHTMLAttributes } from 'react';
import type { Components } from 'react-markdown';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown.js';
import type { ExecutiveSummaryCitation } from '@trinity/chat/types/segment-executive-summary';
import CitationRun from './CitationRun';
import {
  CITATION_RUN_HREF,
  extractCitationNumberFromHref,
  extractRunIndex,
  parseCitationRuns,
} from './parseCitationRuns';

type MaybeComparisonCitation = ExecutiveSummaryCitation & { segment_name?: string };

interface Props {
  body: string;
  citations: MaybeComparisonCitation[];
  onCitationClick: (n: number) => void;
}

export default function CitationRunRenderer({
  body,
  citations,
  onCitationClick,
}: Props): React.JSX.Element {
  const { markdown, runs } = useMemo(() => parseCitationRuns(body), [body]);

  const citationsByNumber = useMemo(() => {
    const map = new Map<number, MaybeComparisonCitation>();
    for (const c of citations) map.set(c.number, c);
    return map;
  }, [citations]);

  const components = useMemo<Components>(() => {
    const AnchorOverride = ({
      href,
      children,
      ...rest
    }: AnchorHTMLAttributes<HTMLAnchorElement>): React.JSX.Element => {
      if (href === CITATION_RUN_HREF) {
        const label = typeof children === 'string' ? children : extractChildText(children);
        const runIndex = extractRunIndex(label ?? '');
        if (runIndex != null && runs[runIndex]) {
          return (
            <CitationRun
              numbers={runs[runIndex]}
              citationsByNumber={citationsByNumber}
              onCitationClick={onCitationClick}
            />
          );
        }
      }

      const citeNumber = extractCitationNumberFromHref(href);
      if (citeNumber != null) {
        return (
          <a
            href={href}
            onClick={(e) => {
              e.preventDefault();
              onCitationClick(citeNumber);
            }}
            {...rest}
          >
            {children}
          </a>
        );
      }

      return (
        <a href={href} {...rest}>
          {children}
        </a>
      );
    };

    return { a: AnchorOverride };
  }, [runs, citationsByNumber, onCitationClick]);

  return <ChatMarkdown value={markdown} components={components} />;
}

function extractChildText(children: React.ReactNode): string | null {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map((c) => (typeof c === 'string' ? c : '')).join('');
  }
  return null;
}
